import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, CalendarClock, Building2 } from "lucide-react";
import { Reveal } from "./Reveal";
import { formatDate } from "../data/posts";
import { useNiches, usePosts } from "../hooks/useContent";
import { scrollToId } from "../lib/nav";

const MtdCard = () => (
  <Link
    to="/making-tax-digital"
    data-testid="toolkit-mtd-card"
    className="group relative lg:col-span-8 rounded-3xl bg-[#103080] text-white p-8 sm:p-10 overflow-hidden lift-shadow flex flex-col justify-between min-h-[300px]"
  >
    <div className="absolute -right-16 -top-16 w-72 h-72 rounded-full bg-[#0090D0]/30 blur-3xl transition-transform duration-700 group-hover:scale-125" />
    <img src="/logo-mark-hd.png" alt="" aria-hidden="true" className="absolute right-6 bottom-6 w-40 opacity-10 pointer-events-none" />
    <div className="relative">
      <span className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-3.5 py-1.5 text-[11px] uppercase tracking-[0.2em] text-[#40C0E0]">
        <CalendarClock size={13} /> Now live · April 2026
      </span>
      <h3 className="font-serif font-semibold text-3xl sm:text-4xl leading-tight mt-6 max-w-lg">
        Making Tax Digital for Income Tax — are you ready?
      </h3>
      <p className="mt-4 text-white/70 text-sm sm:text-base max-w-lg leading-relaxed">
        Sole traders and landlords earning over £50,000 now keep digital records and send HMRC quarterly updates. Our roadmap walks you through every date.
      </p>
    </div>
    <div className="relative mt-8 flex flex-wrap items-center gap-x-6 gap-y-3">
      <span className="inline-flex items-center gap-2 text-sm font-medium text-white group-hover:text-[#40C0E0] transition-colors duration-300">
        Read the MTD guide <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </span>
      <span className="text-xs text-white/45">Free downloadable roadmap · Quarterly updates due 7 Aug · 7 Nov · 7 Feb · 7 May</span>
    </div>
  </Link>
);

const AllBusinessCard = () => (
  <button
    type="button"
    onClick={() => scrollToId("#contact")}
    data-testid="toolkit-all-business-card"
    className="group lg:col-span-4 text-left rounded-3xl bg-white border border-[#103080]/10 p-8 sm:p-10 lift-shadow flex flex-col justify-between min-h-[300px]"
  >
    <div>
      <span className="w-12 h-12 rounded-2xl bg-[#F5F2EB] flex items-center justify-center text-[#103080]">
        <Building2 size={22} strokeWidth={1.75} />
      </span>
      <h3 className="font-serif font-semibold text-2xl text-[#103080] leading-tight mt-6">Don't see your business type?</h3>
      <p className="mt-3 text-sm text-[#0F172A]/65 leading-relaxed">
        We work with every kind of business — from one-person consultancies to growing limited companies. The specialisms below are just where we've gone deepest.
      </p>
    </div>
    <span className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-[#103080] group-hover:text-[#0090D0] transition-colors duration-300">
      Tell us what you do <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
    </span>
  </button>
);

const NicheTile = ({ niche, i }) => (
  <Reveal delay={i * 0.08} className="h-full">
    <Link
      to={`/toolkit/${niche.slug}`}
      data-testid={`toolkit-niche-${niche.slug}`}
      className="group h-full flex flex-col rounded-3xl bg-white border border-[#103080]/10 p-7 lift-shadow"
    >
      <span className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: `${niche.accent}14`, color: niche.accent }}>
        <niche.icon size={22} strokeWidth={1.75} />
      </span>
      <h3 className="font-serif font-semibold text-xl text-[#103080] mt-6">{niche.name}</h3>
      <p className="text-sm text-[#0F172A]/60 mt-1.5 leading-relaxed">{niche.short}</p>
      <span className="mt-auto pt-6 inline-flex items-center gap-2 text-sm font-medium text-[#0090D0]">
        {niche.guides.length} free guides <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
      </span>
    </Link>
  </Reveal>
);

const BlogStrip = () => {
  const POSTS = usePosts();
  const latest = [...POSTS].sort((a, b) => b.date.localeCompare(a.date)).slice(0, 3);
  return (
    <Reveal delay={0.1}>
      <div className="mt-16 sm:mt-20 pt-10 border-t border-[#103080]/10" data-testid="toolkit-blog-strip">
        <div className="flex items-end justify-between gap-6 mb-8">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#0090D0] font-medium mb-3">From the blog</p>
            <h3 className="font-serif font-semibold tracking-tight text-[#103080] text-2xl sm:text-3xl">Practical guides to help your business thrive.</h3>
          </div>
          <Link
            to="/blog"
            data-testid="blog-view-all-link"
            className="group hidden sm:inline-flex items-center gap-2 text-sm font-medium text-[#103080] hover:text-[#0090D0] transition-colors duration-300 shrink-0"
          >
            Read all posts <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {latest.map((p) => (
            <Link
              key={p.slug}
              to={`/blog/${p.slug}`}
              data-testid={`blog-card-${p.slug}`}
              className="group flex md:flex-col gap-4 rounded-2xl bg-white border border-[#103080]/10 p-4 lift-shadow"
            >
              <img src={p.image} alt="" className="w-24 h-24 md:w-full md:h-40 object-cover rounded-xl shrink-0 transition-transform duration-500 group-hover:scale-[1.02]" />
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-[0.2em] text-[#0F172A]/45">{formatDate(p.date)} · {p.readTime}</p>
                <h4 className="font-serif font-semibold text-[#103080] text-base sm:text-lg leading-snug mt-1.5 group-hover:text-[#0090D0] transition-colors duration-300 line-clamp-3">{p.title}</h4>
              </div>
            </Link>
          ))}
        </div>
        <Link to="/blog" className="sm:hidden mt-6 inline-flex items-center gap-2 text-sm font-medium text-[#103080]">
          Read all posts <ArrowRight size={16} />
        </Link>
      </div>
    </Reveal>
  );
};

export const BusinessToolkit = () => {
  const NICHES = useNiches();
  return (
  <section id="toolkit" data-testid="toolkit-section" className="py-24 sm:py-36 bg-[#F5F2EB] border-y border-[#103080]/10">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <Reveal>
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12 sm:mb-16">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#0090D0] font-medium mb-5">Business Toolkit</p>
            <h2 className="font-serif font-semibold tracking-tight text-[#103080] text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-2xl">
              Free guides for your kind of business.
            </h2>
          </div>
          <p className="text-base sm:text-lg text-[#0F172A]/65 leading-relaxed max-w-md lg:text-right">
            We help every kind of business — and we've built up specialist know-how in a few. Pick yours below, or just tell us what you do.
          </p>
        </div>
      </Reveal>

      <Reveal>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <MtdCard />
          <AllBusinessCard />
        </div>
      </Reveal>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-6">
        {NICHES.map((n, i) => <NicheTile key={n.slug} niche={n} i={i} />)}
      </div>

      <BlogStrip />
    </div>
  </section>
  );
};
